import React from "react";
import { Page, Text, View, Document, StyleSheet, Image } from "@react-pdf/renderer";
import paymentMethods from "utils/static/paymentMethods";
import { hasElements } from "utils/arrays";
import { dateToString } from "utils/dateUtils";
import { Tenant } from "utils/tenants";
import { formatCurrency } from "utils/currency";

const getPaymentMethodName = (paymentMethod) => (paymentMethod ? paymentMethods.find((x) => x.value === paymentMethod).label : "");
const Money = ({ amount, currency }) => formatCurrency(amount, currency.locale, currency.isoCode);

// Create styles
const styles = StyleSheet.create({
    page: {
        paddingTop: 65,
        paddingBottom: 65,
        paddingHorizontal: 60,
    },
    header: {
        fontSize: 10,
        flexDirection: "row",
        marginBottom: 10,
        justifyContent: "flex-start",
    },
    image: {
        height: 64,
    },
    companyInfo: {
        marginLeft: 10,
    },
    companyName: {
        fontSize: 14,
        fontWeight: "bold",
    },
    title: {
        fontSize: 20,
    },
    divider: {
        width: "100%",
        height: 1,
        backgroundColor: "black",
        marginVertical: 10,
    },
    customerInfo: {
        fontSize: 10,
        fontWeight: "normal",
        flexDirection: "row",
        marginVertical: 20,
        justifyContent: "space-between",
    },
    customerName: {
        fontSize: 18,
        fontWeight: "bold",
    },
    fileInfo: {
        flexDirection: "row",
    },

    receivedSection: {
        fontSize: 14,
        fontWeight: "normal",
        flexDirection: "row",
        marginTop: 50,
        marginLeft: 10,
        justifyContent: "flex-start",
        width: "60%",
    },

    signature: {
        flex: 1,
        textAlign: "center",
    },

    userName: {
        fontSize: 8,
    },

    balance: {
        flexDirection: "row-reverse",
        marginTop: 10,
    },

    pageNumber: {
        position: "absolute",
        fontSize: 10,
        bottom: 30,
        left: 0,
        right: 0,
        textAlign: "center",
        color: "grey",
    },

    footer: {
        position: "absolute",
        bottom: 80,
        left: 60,
        right: 60,
    },

    detail: {
        width: "100%",
    },

    tableHead: {
        flexDirection: "row",
        alignContent: "flex-start",
        marginBottom: 10,
    },

    columnHeader: {
        fontSize: 12,
        fontWeight: "bold",
        color: "blue",
        fontStyle: "italic",
        padding: 2,
        flex: 1,
        textAlign: "left",
        marginLeft: 10,
    },

    row: {
        flexDirection: "row",
        alignContent: "flex-start",
    },

    cell: {
        fontSize: 11,
        padding: 2,
        flex: 1,
        textAlign: "left",
        marginLeft: 10,
    },

    disclaimer: {
        fontSize: 10,
        fontWeight: "bold",
        fontStyle: "italic",
        textAlign: "left",
        marginTop: 30,
        marginLeft: 10,
    },

    notesSection: {
        fontSize: 12,
        fontWeight: "light",
        flexDirection: "column",
        marginTop: 20,
        marginLeft: 10,
        justifyContent: "flex-start",
        width: "90%",
    },

    notesTitle: {
        fontWeight: "bold",
        marginTop: 10,
    },
});

// Create Document Component
const MyDocument = ({ receipt, resources, documentIssuer, currency }) => {
    const getTotal = () => {
        if (hasElements(receipt.detailEntries)) {
            return receipt.detailEntries.reduce((acc, item) => acc + item.amount, 0);
        }
        return 0;
    };

    const getCategoryName = (categoryId) => {
        if (resources && hasElements(resources.categories)) {
            return resources.categories.find((x) => x.id === categoryId).name;
        }
        return "";
    };

    return (
        <Document>
            <Page key={receipt.id} size="A4" style={styles.page}>
                <View style={styles.header} fixed>
                    {<Image style={styles.image} src={Tenant.getLogo()} />}

                    <View style={styles.companyInfo}>
                        <Text fixed style={styles.companyName}>
                            {documentIssuer?.name}
                        </Text>

                        <Text fixed>{documentIssuer?.address}</Text>
                        <Text fixed>Phone: {documentIssuer?.phoneNumber}</Text>
                        <Text fixed>Fax: {documentIssuer?.fax}</Text>
                        <Text fixed>Email: {documentIssuer?.email}</Text>
                    </View>
                    <View style={{ flex: 1 }} />
                    <View>
                        <Text fixed style={styles.title}>
                            Receipt
                        </Text>
                        <Text fixed style={{ fontSize: 10, alignSelf: "flex-end" }}>
                            No. {receipt.id}
                        </Text>
                        <Text fixed style={{ fontSize: 10, alignSelf: "flex-end" }}>
                            {dateToString(new Date(), false)}
                        </Text>
                    </View>
                </View>

                <View style={styles.divider} fixed />

                <View style={{ ...styles.divider, marginTop: 0, borderWidth: 2 }} fixed />

                <View style={styles.detail}>
                    <View style={styles.tableHead}>
                        <View style={{ ...styles.columnHeader, flexBasis: "50%" }}>
                            <Text>Category</Text>
                        </View>
                        <View style={{ ...styles.columnHeader, textAlign: "right", flexBasis: "20%" }}>
                            <Text>Amount</Text>
                        </View>
                        <View style={{ ...styles.columnHeader, flexBasis: "30%" }}>
                            <Text>Payment Method</Text>
                        </View>
                        {/* <View style={{ ...styles.columnHeader, flexBasis: "30%" }}>
                            <Text>Notes</Text>
                        </View> */}
                    </View>
                    {hasElements(receipt.detailEntries) &&
                        receipt.detailEntries.map((entry) => (
                            <View style={styles.row} key={entry.id}>
                                <View style={{ ...styles.cell, flexBasis: "50%" }}>
                                    <Text>{getCategoryName(entry.categoryId)}</Text>
                                </View>
                                <View
                                    style={{
                                        ...styles.cell,
                                        textAlign: "right",
                                        fontWeight: "bold",
                                        flexBasis: "20%",
                                    }}
                                >
                                    <Text>
                                        {entry.amount && Number(entry.amount) > 0 ? (
                                            <Money amount={entry.amount} currency={currency} />
                                        ) : (
                                            ""
                                        )}
                                    </Text>
                                </View>
                                <View style={{ ...styles.cell, flexBasis: "30%" }}>
                                    <Text>{getPaymentMethodName(entry.paymentMethod)}</Text>
                                </View>
                                {/* <View style={{ ...styles.cell, flexBasis: "30%" }}>
                                    <Text>{entry.notes}</Text>
                                </View> */}
                            </View>
                        ))}
                </View>

                <View style={styles.footer}>
                    <View style={styles.divider} />
                    <View style={styles.balance}>
                        <Text style={{ fontSize: 14 }}>
                            <Money amount={getTotal()} currency={currency} />
                        </Text>
                        <Text style={{ fontSize: 12, marginRight: 20 }}>Total:</Text>
                    </View>
                    <View style={styles.notesSection}>
                        <View>
                            <Text style={{ fontSize: 12, marginRight: 10 }}>NOTES:</Text>
                        </View>
                        <View>
                            <Text style={styles.notesTitle}>{receipt.notes}</Text>
                        </View>
                    </View>
                    <View style={styles.receivedSection}>
                        <Text style={{ fontSize: 12, marginRight: 10 }}>Received By:</Text>
                        <View style={styles.signature}>
                            <View style={styles.divider} />
                            <Text style={styles.userName}>{receipt.userName}</Text>
                        </View>
                    </View>
                    <View style={styles.disclaimer}>
                        <Text>Keep this receipt for your records as proof of payment.</Text>
                    </View>
                </View>

                <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
            </Page>
        </Document>
    );
};

export default MyDocument;
